/**
 * SurfaceContext — TREATX L1-F canonical surface consumer (frontend).
 *
 * Consumes the server-authoritative `/api/surface/context` payload.
 * NEVER decides the surface locally. NEVER trusts client-side geo /
 * language / locale as residence — matches backend Owner directives §5.
 *
 * Public API:
 *   • useSurface()                  → { surface, rules, resolutionSource, resolutionReason, regulatedDefaultDeny }
 *   • useSurfaceRule('before_after')→ boolean (true = allowed, false = hide)
 *
 * The provider fetches once on mount and re-fetches when a `refresh()`
 * is emitted (e.g. after login / logout / residence declaration).
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '../utils/api';

const SurfaceCtx = createContext({
  surface: 'GLOBAL',
  rules: {},
  resolutionSource: 'client_default',
  resolutionReason: 'not-yet-resolved',
  regulatedDefaultDeny: true,      // Fail closed until the server answers.
  loaded: false,
  refresh: () => {},
});

const DEFAULT_FAIL_CLOSED_RULES = {
  before_after: false,
  sponsored_featuring: false,
  promotional_price: false,
  discounts: false,
  campaigns: false,
  patient_testimonials: false,
  ratings_display: false,
  paid_promotional_targeting: false,
  social_links: false,
};

export const SurfaceProvider = ({ children }) => {
  const [state, setState] = useState({
    surface: 'GLOBAL',
    rules: DEFAULT_FAIL_CLOSED_RULES,
    resolutionSource: 'client_default',
    resolutionReason: 'not-yet-resolved',
    regulatedDefaultDeny: true,
    loaded: false,
  });

  const fetchCtx = useCallback(async () => {
    try {
      const r = await api.get('/surface/context');
      const d = r.data || {};
      setState({
        surface: d.surface || 'GLOBAL',
        rules: d.rules || DEFAULT_FAIL_CLOSED_RULES,
        resolutionSource: d.resolution_source || 'unknown',
        resolutionReason: d.resolution_reason || '',
        regulatedDefaultDeny: !!d.regulated_default_deny,
        loaded: true,
      });
    } catch (e) {
      // Any error → keep fail-closed defaults; the FE hides regulated
      // content rather than showing it under an unresolved surface.
      setState((s) => ({ ...s, loaded: true, resolutionSource: 'error', resolutionReason: e?.message || 'fetch_failed' }));
    }
  }, []);

  useEffect(() => { fetchCtx(); }, [fetchCtx]);

  const value = useMemo(() => ({ ...state, refresh: fetchCtx }), [state, fetchCtx]);

  return <SurfaceCtx.Provider value={value}>{children}</SurfaceCtx.Provider>;
};

export const useSurface = () => useContext(SurfaceCtx);

/**
 * useSurfaceRule('before_after') → boolean. `true` means allowed on
 * the current surface; `false` means hide. Matches the backend rule
 * matrix so FE and BE render the same decision.
 */
export const useSurfaceRule = (name) => {
  const { surface, rules, regulatedDefaultDeny, loaded } = useContext(SurfaceCtx);
  // Until the server answers, keep regulated content hidden. Non-regulated
  // capabilities behave conservatively for GLOBAL until confirmed.
  if (!loaded) return false;
  if (regulatedDefaultDeny) return false;
  if (surface === 'GLOBAL') return true;
  // TR — consult the matrix. Unknown rule keys default to `false` (fail closed).
  return rules && typeof rules[name] === 'boolean' ? rules[name] : false;
};

export default SurfaceProvider;
